import React, { useMemo, useState } from 'react';
import axios from 'axios';

import CardDialog from '~/components/CardDialog';
import Button from '~/components/core/Atomic/Buttons/Button';
import Chip from '~/components/core/Atomic/Chip/Chip';
import CancelButton from '~/components/core/Buttons/CancelButton';
import MultiSelectField from '~/components/core/Molecules/Fields/MultiSelectField';
import TextField from '~/components/core/Molecules/Fields/TextField';
import { FeedbackSnackbar, useSnackbar } from '~/components/core/Molecules/Snackbar';
import SkeletonTable from '~/components/core/Skeletons/SkeletonTable';
import { Text } from '~/components/core/TextComponents';
import { getTokensUrl } from '~/components/GenericTemplates/utils/routes';
import { useLobConfiguration } from '~/components/hooks/useLobConfiguration';
import { CopyToClipboardIcon, DownloadIcon } from '~/components/icons';
import OverflowTextWithToolTip from '~/components/OverflowTextWithToolTip';
import PlainTable from '~/components/PlainTable';
import OperationsBreadcrumbs from '~/components/SystemConfiguration/OperationsBreadcrumbs';
import { useSysconfig } from '~/components/SystemConfiguration/SystemConfigurationScreen';
import useDataFetcher from '~/components/useDataFetcher';
import type {
  GenericTemplatesTokensResponse,
  GenericTemplatesTokensResponseGenericTemplatesTokenResponse,
} from '~/server_shared/openapi/data-contracts';
import colors from '~/theme/tailwind/colors';
import { getAxiosParamsSerializer } from '~/Utils';
import { getLobDescription } from '~/Utils/lobUtils';

import { buildTokensCsv, downloadCsvFile, FEATURE_DISPLAY_NAME, getTokensExportFilename } from './tokensExport';

type TokenResponse = GenericTemplatesTokensResponseGenericTemplatesTokenResponse;

const FETCH_PARAMS_SERIALIZER = getAxiosParamsSerializer('none');

const EMPTY_TOKENS: TokenResponse[] = [];

const TokensViewerPage: React.FC = () => {
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  const { organization } = useSysconfig();
  const { lobConfigurationsDict } = useLobConfiguration();

  const [searchText, setSearchText] = useState('');
  const [selectedLobs, setSelectedLobs] = useState<string[]>([]);
  const [selectedSubOrgIds, setSelectedSubOrgIds] = useState<number[]>([]);
  const [isExporting, setIsExporting] = useState(false);
  const [isExportDialogOpen, setIsExportDialogOpen] = useState(false);
  const { snackbarState, showSnackbar, closeSnackbar } = useSnackbar();

  // Get available LOBs from organization
  const availableLobs = organization?.supported_claim_types || [];

  // Get available sub-organizations
  const availableSubOrgs = organization?.sub_organizations_enabled ? organization.sub_organizations || [] : [];

  // Build API params - empty arrays mean "show all"
  const apiParams = useMemo(() => {
    const params: Record<string, unknown> = {
      is_claim_level: false, // false = show both claim-level and exposure-level tokens
      filtering_mode: 'union',
    };

    // Only include lobs param if some are selected (empty = show all)
    if (selectedLobs.length > 0) {
      params.lobs = selectedLobs;
    }

    // Only include sub_organization_ids if some are selected (empty = show all)
    if (selectedSubOrgIds.length > 0) {
      params['sub_organization_ids[]'] = selectedSubOrgIds;
    }

    return params;
  }, [selectedLobs, selectedSubOrgIds]);

  const { data, isLoading, isError } = useDataFetcher<GenericTemplatesTokensResponse>(getTokensUrl(organization.id), {
    params: apiParams,
    paramsSerializer: FETCH_PARAMS_SERIALIZER,
  });

  const tokens = data?.tokens || EMPTY_TOKENS;

  // `tokens` is filtered by the server-side LOB / sub-org params, but the export always
  // fetches the unfiltered list. Only treat an empty result as "nothing to export" when no
  // such filters are active — otherwise a filtered-to-zero view would wrongly block a download
  // that would still contain tokens.
  const hasServerSideFilters = selectedLobs.length > 0 || selectedSubOrgIds.length > 0;

  // The export runs its own request in `handleExport`, independent of the table fetch, so a
  // failed list fetch must not disable it (it could still succeed). Only block the button when
  // the table fetch actually succeeded and confirmed there is nothing to export.
  const hasNothingToExport = !isError && !hasServerSideFilters && tokens.length === 0;

  const filteredTokens = useMemo(() => {
    if (!searchText) {
      return tokens;
    }
    const lowerSearch = searchText.toLowerCase();
    return tokens.filter((token) => {
      // Search in token_key and display_name
      if (
        token.token_key.toLowerCase().includes(lowerSearch) ||
        token.display_name.toLowerCase().includes(lowerSearch)
      ) {
        return true;
      }
      // Search in combined token_group + display_name (like TipTap)
      if (token.token_group && token.token_group.length > 0) {
        const searchable = [...token.token_group, token.display_name].join(' ').toLowerCase();
        if (searchable.includes(lowerSearch)) {
          return true;
        }
      }
      // Search in tags
      if (token.tags?.some((tag) => tag.toLowerCase().includes(lowerSearch))) {
        return true;
      }
      return false;
    });
  }, [tokens, searchText]);

  const handleCopyToken = async (tokenKey: string) => {
    const formattedToken = `{{ ${tokenKey} }}`;
    try {
      await navigator.clipboard.writeText(formattedToken);
      showSnackbar('Token copied to clipboard', 'success');
    } catch (error) {
      showSnackbar('Failed to copy token', 'error');
    }
  };

  const handleExport = async () => {
    if (!organization) {
      return;
    }
    setIsExporting(true);
    try {
      // Always export the complete token list, ignoring the page's LOB / sub-org / search filters.
      const response = await axios.get<GenericTemplatesTokensResponse>(getTokensUrl(organization.id), {
        params: { is_claim_level: false, filtering_mode: 'union' },
        paramsSerializer: FETCH_PARAMS_SERIALIZER,
      });
      const csv = buildTokensCsv(response.data?.tokens || EMPTY_TOKENS, {
        includeSubOrganizations: !!organization.sub_organizations_enabled,
        orgLobsCount: organization.supported_claim_types?.length || 0,
      });
      downloadCsvFile(getTokensExportFilename(), csv);
      setIsExportDialogOpen(false);
    } catch (error) {
      showSnackbar('Failed to export tokens', 'error');
    } finally {
      setIsExporting(false);
    }
  };

  const columnData = [
    {
      id: 'token_key',
      label: 'Key',
      specialCell: (token: TokenResponse) => {
        const tokenText = `{{\u00A0${token.token_key}\u00A0}}`;
        return (
          <OverflowTextWithToolTip maxWidth={200} tooltipTitle={tokenText}>
            <code className="whitespace-nowrap rounded bg-slate-100 px-4 py-2">{tokenText}</code>
          </OverflowTextWithToolTip>
        );
      },
    },
    {
      id: 'display_name',
      label: 'Display Name',
      specialCell: (token: TokenResponse) => (
        <div>
          <div className="flex items-center gap-4">
            <span>{token.display_name}</span>
            {token.tags?.map((tag) => <Chip key={tag} label={tag} size="small" variant="outlined" />)}
          </div>
          {token.token_group && token.token_group.length > 0 && (
            <div className="text-xs text-slate-700">{token.token_group.join(' › ')}</div>
          )}
        </div>
      ),
    },
    {
      id: 'lobs',
      label: 'LOB',
      disableSort: true,
      specialCell: (token: TokenResponse) => {
        if (!token.lobs || token.lobs.length === 0) {
          return 'All';
        }
        const lobNames = token.lobs.map((lob) => lob.display_name || lob.key);
        // If it has all org LOBs, show "All"
        const orgLobsCount = organization.supported_claim_types?.length || 0;
        if (lobNames.length >= orgLobsCount && orgLobsCount > 0) {
          return 'All';
        }
        return (
          <div className="flex flex-wrap gap-4">
            {lobNames.slice(0, 3).map((name, idx) => (
              <Chip key={idx} label={name} size="small" className="mx-2" />
            ))}
            {lobNames.length > 3 && <Chip label={`+${lobNames.length - 3} more`} size="small" className="mx-2" />}
          </div>
        );
      },
    },
    {
      id: 'sub_organizations',
      label: 'Sub-org',
      disableSort: true,
      isHidden: !organization.sub_organizations_enabled,
      specialCell: (token: TokenResponse) => {
        if (!token.sub_organizations || token.sub_organizations.length === 0) {
          return 'All';
        }
        const subOrgNames = token.sub_organizations.map((subOrg) => subOrg.name);
        return (
          <div className="flex flex-wrap gap-4">
            {subOrgNames.slice(0, 2).map((name, idx) => (
              <Chip key={idx} label={name} size="small" className="mx-2" />
            ))}
            {subOrgNames.length > 2 && <Chip label={`+${subOrgNames.length - 2} more`} size="small" className="mx-2" />}
          </div>
        );
      },
    },
    {
      id: 'feature_availability',
      label: 'Feature Availability',
      disableSort: true,
      specialCell: (token: TokenResponse) => {
        const extendedToken = token as TokenResponse & {
          feature_specific?: string[];
          exclude_feature_specific?: string[];
        };
        const featureSpecific = extendedToken.feature_specific;
        const excludeFeatureSpecific = extendedToken.exclude_feature_specific;

        const hasOnly = featureSpecific && featureSpecific.length > 0;
        const hasExclude = excludeFeatureSpecific && excludeFeatureSpecific.length > 0;

        if (!hasOnly && !hasExclude) {
          return null;
        }

        return (
          <div className="flex flex-wrap gap-4">
            {hasOnly &&
              featureSpecific.map((f) => (
                <Chip
                  key={`only-${f}`}
                  label={`Only: ${FEATURE_DISPLAY_NAME[f] || f}`}
                  size="small"
                  variant="outlined"
                  className="mx-2"
                />
              ))}
            {hasExclude &&
              excludeFeatureSpecific.map((f) => (
                <Chip
                  key={`exclude-${f}`}
                  label={`Exclude: ${FEATURE_DISPLAY_NAME[f] || f}`}
                  size="small"
                  variant="outlined"
                  className="mx-2"
                />
              ))}
          </div>
        );
      },
    },
    {
      id: 'actions',
      label: 'Actions',
      disableSort: true,
      specialCell: (token: TokenResponse) => (
        <Button
          variant="text"
          onClick={(e: React.MouseEvent) => {
            e.stopPropagation();
            handleCopyToken(token.token_key);
          }}
          className="min-w-0 p-4"
          aria-label={`Copy token ${token.token_key}`}
        >
          <CopyToClipboardIcon />
        </Button>
      ),
    },
  ];

  const displayLoading = isLoading || isError;

  return (
    <div className="m-20">
      <div className="mb-12">
        <OperationsBreadcrumbs
          currentTab="Tokens"
          intermediateTabs={[{ label: 'Templates', to: '/sysconfig/operations/generic_templates_configuration' }]}
        />
      </div>

      <CardDialog
        title="Tokens"
        action={
          <div className="flex items-center justify-end">
            <Button
              color="primary"
              onClick={() => setIsExportDialogOpen(true)}
              disabled={isLoading || isExporting || hasNothingToExport}
            >
              <DownloadIcon iconColor={colors.teal['700']} size={12} />
              <span className="ml-8">Export</span>
            </Button>
          </div>
        }
      >
        {/* Filters row */}
        <div className="mb-16 flex items-end gap-16">
          <TextField
            placeholder="Search tokens..."
            value={searchText}
            onChange={(newValue: string) => setSearchText(newValue)}
            className="w-[300px]"
          />

          {availableLobs.length > 1 && (
            <MultiSelectField
              id="lob-filter"
              label="LOB"
              options={availableLobs}
              value={selectedLobs}
              onChange={(newValues: string[]) => setSelectedLobs(newValues)}
              renderValue={(values: string[]) =>
                values.length === 0
                  ? 'All LOBs'
                  : values.map((lob) => getLobDescription(lob, lobConfigurationsDict)).join(', ')
              }
              renderOption={(lob: string) => getLobDescription(lob, lobConfigurationsDict)}
              className="w-[200px]"
            />
          )}

          {availableSubOrgs.length > 0 && (
            <MultiSelectField
              id="sub-org-filter"
              label="Sub-organization"
              options={availableSubOrgs.map((so: { id: number }) => String(so.id))}
              value={selectedSubOrgIds.map(String)}
              onChange={(newValues: string[]) => setSelectedSubOrgIds(newValues.map(Number))}
              renderValue={(values: string[]) => {
                if (values.length === 0) return 'All Sub-orgs';
                return values
                  .map((id) => availableSubOrgs.find((so: { id: number }) => so.id === Number(id))?.name || id)
                  .join(', ');
              }}
              renderOption={(id: string) =>
                availableSubOrgs.find((so: { id: number }) => so.id === Number(id))?.name || id
              }
              className="w-[200px]"
            />
          )}

          <Text variant={Text.VARIANTS.SM} colorVariant={Text.COLOR_VARIANTS.SECONDARY} className="ml-auto">
            {filteredTokens.length} token{filteredTokens.length !== 1 ? 's' : ''} found
          </Text>
        </div>

        {displayLoading ? (
          <SkeletonTable rowsCount={10} columnsCount={5} isError={isError} />
        ) : (
          <PlainTable
            rows={filteredTokens}
            columns={columnData.filter((column) => !column.isHidden)}
            keepRowsOrder
            stickyHeader
            autoPaginateRowsPerPage={25}
          />
        )}
      </CardDialog>

      {isExportDialogOpen && (
        <CardDialog
          isDialog
          maxWidth="sm"
          fullWidth
          title="Export tokens"
          onClose={() => setIsExportDialogOpen(false)}
          preventClose={isExporting}
          footerActions={
            <>
              <CancelButton withMarginRight disabled={isExporting} onClick={() => setIsExportDialogOpen(false)} />
              <Button variant="contained" color="primary" onClick={handleExport} disabled={isExporting}>
                Export
              </Button>
            </>
          }
        >
          <Text variant={Text.VARIANTS.SM}>
            Export all tokens to a CSV file. The file includes every token across all lines of business and
            sub-organizations, regardless of the filters applied on this page.
          </Text>
        </CardDialog>
      )}

      <FeedbackSnackbar state={snackbarState} onClose={closeSnackbar} />
    </div>
  );
};

export default TokensViewerPage;
